import React from 'react';

interface Player {
  id: string;
  name: string;
  score?: number;
  avatar?: string;
}

interface PlayersListProps {
  players: Player[];
  variant?: 'compact' | 'detailed' | 'grid';
  showScores?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

export const PlayersList: React.FC<PlayersListProps> = ({
  players = [],
  variant = 'compact',
  showScores = false,
  className,
  style,
}) => {
  const renderAvatar = (avatar?: string) => {
    if (avatar && (avatar.startsWith('/') || avatar.startsWith('data:') || avatar.includes('.svg'))) {
      const size = variant === 'compact' ? '1.3rem' : variant === 'grid' ? '1.8rem' : '1.5rem';
      return (
        <img 
          src={avatar} 
          alt="" 
          style={{ 
            width: size, 
            height: size, 
            objectFit: 'contain',
            marginRight: variant === 'grid' ? '0.5rem' : '0',
          }} 
        />
      );
    }
    const fontSize = variant === 'compact' ? '1rem' : variant === 'grid' ? '1.5rem' : '1.2rem';
    return (
      <span style={{ 
        fontSize,
        marginRight: variant === 'grid' ? '0.5rem' : '0',
      }}>
        {avatar || '👤'}
      </span>
    );
  };

  const getContainerStyle = (): React.CSSProperties => {
    const baseStyle: React.CSSProperties = {
      display: 'flex',
      alignItems: 'center',
      gap: variant === 'compact' ? '0.3rem' : variant === 'grid' ? '0.5rem' : '0.5rem',
      padding: variant === 'compact' ? '0.4rem 0.8rem' : variant === 'grid' ? '0.5rem' : '0.5rem',
      backgroundColor: variant === 'grid' ? 'rgba(248, 250, 252, 0.1)' : 'rgba(248, 250, 252, 0.08)',
      borderRadius: variant === 'compact' ? '20px' : variant === 'grid' ? '8px' : '8px',
      fontSize: variant === 'compact' ? '0.9rem' : variant === 'grid' ? '1rem' : '1rem',
      color: 'rgba(255, 255, 255, 0.9)',
      border: '1px solid rgba(248, 250, 252, 0.1)',
      margin: 0,
      minWidth: 0, // Allow flex items to shrink below their content size
      width: '100%', // Take full width of grid cell
      maxWidth: '100%', // Prevent overflow
      overflow: 'hidden', // Hide overflow
      boxSizing: 'border-box', // Include padding in width calculation
      lineHeight: '1.2', // Prevent extra height from default line-height
      height: 'auto', // Ensure height is based on content
      minHeight: 'auto', // Don't force minimum height
    };

    if (variant === 'detailed') {
      baseStyle.backgroundColor = 'rgba(255, 255, 255, 0.05)';
    }

    return baseStyle;
  };

  const getListStyle = (): React.CSSProperties => {
    const baseStyle: React.CSSProperties = {
      listStyle: 'none',
      padding: 0,
      margin: 0,
    };

    if (variant === 'grid') {
      return {
        ...baseStyle,
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
        gap: '0.4rem',
      };
    }

    return {
      ...baseStyle,
      display: 'flex',
      flexWrap: 'wrap',
      gap: variant === 'compact' ? '0.25rem' : '0.4rem',
      justifyContent: 'center',
    };
  };

  // Empty state is now handled by PlayerListSection
  if (players.length === 0) {
    return null;
  }

  return (
    <div className={`players-container ${className || ''}`} style={{ ...getListStyle(), ...style }}>
      {players.map((player) => (
        <div key={player.id} className="player-item" style={getContainerStyle()}>
          {renderAvatar(player.avatar)}
          <span style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            minWidth: 0, // Allow text to shrink
            flex: 1, // Take available space
          }}>{player.name}</span>
          {showScores && player.score !== undefined && (
            <span style={{ marginLeft: 'auto', fontWeight: 'bold' }}>
              {player.score}
            </span>
          )}
        </div>
      ))}
    </div>
  );
};
